import {
  prisma,
  CommunicationChannel,
  CommunicationLevel,
  ResourceType,
  ActionType,
  EmailChannel,
  EmailChannelType,
} from "client";

export type EmailChannelWithProviders = EmailChannel & {
  sendGridChannel?: {
    apiKey: string;
    senderEmail: string;
    fromName?: string | null;
  } | null;
  nodeMailerChannel?: {
    host: string;
    port: number | string;
    secure?: boolean;
    authUser: string;
    authPass: string;
    senderEmail: string;
    fromName?: string | null;
  } | null;
};

export type EffectiveEmailChannelResult =
  | EmailChannelWithProviders
  | null
  | { error: unknown };

export function isEmailChannelWithProviders(
  value: EffectiveEmailChannelResult | undefined
): value is EmailChannelWithProviders {
  return (
    !!value &&
    typeof value === "object" &&
    "type" in value &&
    "communicationChannelId" in value
  );
}

// CREATE
export const createCommunicationChannel = async (
  entityId: string,
  userId: string,
  level?: CommunicationLevel
) => {
  try {
    let connect = {};
    switch (level) {
      case CommunicationLevel.WHITE_LABEL:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EVENT:
        connect = { event: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.ORGANIZATION:
        connect = { organization: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EXHIBITOR:
        connect = { exhibitor: { connect: { id: entityId } } };
        break;

      default:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
    }

    const communicationChannel = await prisma.communicationChannel.create({
      data: {
        level: level || CommunicationLevel.WHITE_LABEL,
        ...connect,
      },
    });
    return { communicationChannel };
  } catch (error) {
    return { error };
  }
};

// READ
export const getAllCommunicationChannels = async (
  userId: string
): Promise<{
  communicationChannels?: CommunicationChannel[];
  error?: any;
}> => {
  try {
    const communicationChannels = await prisma.communicationChannel.findMany();
    return { communicationChannels };
  } catch (error) {
    return { error };
  }
};

export const getCommunicationChannelById = async (
  userId: string,
  id: string
) => {
  try {
    const communicationChannel = await prisma.communicationChannel.findUnique({
      where: { id },
    });
    return { communicationChannel };
  } catch (error) {
    return { error };
  }
};

const communicationChannelInclude = {
  whatsappChannel: {
    include: {
      twilioChannel: true,
      unifonicChannel: true,
    },
  },
  smsChannel: {
    include: {
      twilioChannel: true,
      unifonicChannel: true,
    },
  },
  emailChannel: {
    include: {
      sendGridChannel: true,
      nodeMailerChannel: true,
    },
  },
} as const;

/** Generic fetcher by level and entity id. Use for WL, Organization, Event, etc. */
export const getCommunicationChannelByLevelAndEntity = async (
  level: CommunicationLevel,
  entityId: string
) => {
  try {
    const communicationChannel = await prisma.communicationChannel.findFirst({
      where: { level, levelEntityID: entityId },
      include: communicationChannelInclude,
    });
    return communicationChannel;
  } catch (error) {
    return { error };
  }
};

export const getCommunicationChannelByWhiteLabelId = async (
  entityId: string
) => {
  return getCommunicationChannelByLevelAndEntity(
    CommunicationLevel.WHITE_LABEL,
    entityId
  );
};

export const getCommunicationChannelByOrganizationId = async (
  entityId: string
) => {
  return getCommunicationChannelByLevelAndEntity(
    CommunicationLevel.ORGANIZATION,
    entityId
  );
};

export type EffectiveEmailChannelOptions = {
  organizationId?: string;
  eventId?: string;
  whiteLabelId?: string;
};

/**
 * Resolve the effective email channel for sending.
 *
 * Preference order:
 * 1. Organization-level emailChannel (override)
 * 2. White-label emailChannel (from org, event, or explicit whiteLabelId)
 *
 * Returns null if no usable email channel is configured.
 */
export const getEffectiveEmailChannel = async (
  options?: EffectiveEmailChannelOptions
): Promise<EffectiveEmailChannelResult> => {
  try {
    let resolvedOrganizationId = options?.organizationId;
    let resolvedWhiteLabelId = options?.whiteLabelId;

    if (options?.eventId) {
      const event = await prisma.event.findUnique({
        where: { id: options.eventId },
        select: { organizationIds: true, whiteLabelId: true },
      });

      // Canonical org for an event is organizationIds[0] (matches admin settings scope).
      if (event?.organizationIds?.[0]) {
        resolvedOrganizationId = event.organizationIds[0];
      }
      if (!resolvedWhiteLabelId) {
        resolvedWhiteLabelId = event?.whiteLabelId ?? undefined;
      }
    }


    if (resolvedOrganizationId) {

      const orgChannel = await getCommunicationChannelByOrganizationId(
        resolvedOrganizationId
      );

      if (orgChannel && !("error" in orgChannel) && orgChannel.emailChannel) {
        const emailChannel =
          orgChannel.emailChannel as EmailChannelWithProviders;

        return emailChannel;
      }

      const organization = await prisma.organization.findUnique({
        where: { id: resolvedOrganizationId },
        select: { whiteLabelId: true },
      });

      if (organization?.whiteLabelId) {
        resolvedWhiteLabelId = organization.whiteLabelId;
      }
    }

    if (resolvedWhiteLabelId) {
      const wlChannel = await getCommunicationChannelByWhiteLabelId(
        resolvedWhiteLabelId
      );

      if (wlChannel && !("error" in wlChannel) && wlChannel.emailChannel) {
        const emailChannel = wlChannel.emailChannel as EmailChannelWithProviders;

        return emailChannel;
      }
    }

    return null;
  } catch (error) {
    return { error };
  }
};

/** @deprecated Prefer getEffectiveEmailChannel({ organizationId, eventId, whiteLabelId }) */
export const getEffectiveEmailChannelForOrganization = async (
  organizationId?: string,
  eventId?: string,
  whiteLabelId?: string
): Promise<EffectiveEmailChannelResult> =>
  getEffectiveEmailChannel({ organizationId, eventId, whiteLabelId });

export const getCommunicationChannelByWhiteLabelIdPublic = async (
  entityId: string
) => {
  try {
    const communicationChannel = await prisma.communicationChannel.findFirst({
      where: {
        level: CommunicationLevel.WHITE_LABEL,
        levelEntityID: entityId,
      },
      include: communicationChannelInclude,
    });
    return communicationChannel;
  } catch (error) {
    return { error };
  }
};
// UPDATE
export const updateCommunicationChannelById = async (
  userId: string,
  id: string,
  data: CommunicationChannel
) => {
  let connect = {};
  switch (data.level) {
    case CommunicationLevel.WHITE_LABEL:
      connect = { whiteLabel: { connect: { id: data.levelEntityID } } };
      break;
    case CommunicationLevel.EVENT:
      connect = { event: { connect: { id: data.levelEntityID } } };
      break;
    case CommunicationLevel.ORGANIZATION:
      connect = { organization: { connect: { id: data.levelEntityID } } };
      break;
    case CommunicationLevel.EXHIBITOR:
      connect = { exhibitor: { connect: { id: data.levelEntityID } } };
      break;

    default:
      connect = { whiteLabel: { connect: { id: data.levelEntityID } } };
      break;
  }
  return await prisma.communicationChannel.update({
    where: { id },
    data: {
      level: data.level,
      ...connect,
    },
  });
};

// DELETE
export const deleteCommunicationChannelById = async (
  userId: string,
  id: string
) => {
  try {
    await prisma.communicationChannel.delete({
      where: { id },
    });
    return { success: true };
  } catch (error) {
    return { error };
  }
};
